// StackedSheet — one reusable component for "a sheet opened from inside
// another sheet" (docs/FIX-SERMON-CLIPS.md §3d, docs/designs/stacked-sheets-prototype.html).
// Ported directly from that prototype's exact transform/transition values and
// pointer-drag math rather than re-derived.
//
// App-wide rule this implements:
//   - A sheet opened from a page: a single sheet, unaffected by this component.
//   - Something opened from inside a sheet: wrap it in <StackedSheet>, and give
//     the sheet BEHIND it the `behindStyle(true)` style below (plus a ref via
//     `backSheetRef` so this component can live-interpolate it during a drag).
//   - A third level: never a third <StackedSheet> — push it inside the current
//     top sheet's own body instead, with a "‹ [previous]" back link (a local
//     view-state swap in that sheet, not a new mount).
//
// Usage:
//   const backRef = useRef(null);
//   <div ref={backRef} className="sheet" style={behindStyle(sermonOpen)}>...</div>
//   <StackedSheet open={sermonOpen} onClose={closeSermon} backSheetRef={backRef}
//     backLabel="John" returnFocusRef={lastRowRef}>
//     <div className="hd">...your own header + ✕ calling onClose...</div>
//     <div className="shbody">...</div>
//   </StackedSheet>
//
// Doesn't apply to: small popovers/menus, confirm dialogs — those aren't this.

import { useEffect, useRef, useState } from "react";

const SLIDE_TRANSITION = "transform .42s cubic-bezier(.2,.8,.2,1)";
const BEHIND_TRANSITION = "transform .42s cubic-bezier(.2,.8,.2,1), filter .42s ease, border-radius .42s ease";

export function prefersReducedMotion() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Style for whatever sheet sits BEHIND a StackedSheet — spread this onto that
// sheet's own wrapping div (in addition to its normal className="sheet"
// styles), toggled by whether something is currently stacked over it.
// `baseTransform` is whatever that sheet's own CSS already relies on for
// positioning (className="sheet" centers itself via `translateX(-50%)`,
// which this must preserve rather than clobber) — pass "" for a full-screen
// sheet that doesn't need one.
export function behindStyle(isBehind, reduced, baseTransform = "translateX(-50%)") {
  if (!isBehind) return { transition: reduced ? "none" : BEHIND_TRANSITION };
  return {
    transform: `${baseTransform} translateY(-10px) scale(0.93)`.trim(),
    filter: "brightness(0.82)",
    borderRadius: "26px 26px 26px 26px",
    transition: reduced ? "none" : BEHIND_TRANSITION,
  };
}

export default function StackedSheet({ open, onClose, backSheetRef, backSheetBaseTransform = "translateX(-50%)", backLabel, returnFocusRef, zIndex = 350, children }) {
  const reduced = prefersReducedMotion();
  // Stays mounted a moment after `open` goes false so the close actually
  // slides down instead of vanishing — mirrors the prototype, where the
  // sermon sheet is never removed from the DOM, just translated off-screen.
  const [shouldRender, setShouldRender] = useState(open);
  const [nudging, setNudging] = useState(false);
  const [toastShown, setToastShown] = useState(false);
  const sheetRef = useRef(null);
  const toastTimerRef = useRef(null);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    if (open) { setShouldRender(true); wasOpenRef.current = true; return; }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    if (reduced) { setShouldRender(false); return; }
    const t = setTimeout(() => setShouldRender(false), 420);
    return () => clearTimeout(t);
  }, [open, reduced]);

  useEffect(() => {
    if (open) { sheetRef.current?.focus?.({ preventScroll: true }); return; }
    returnFocusRef?.current?.focus?.({ preventScroll: true });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Drag the handle down to close — tracks translateY directly and live-
  // interpolates the back sheet's un-shrink via backSheetRef, exactly like
  // the prototype's pointer handlers (imperative style writes, not React
  // state, so it stays smooth at 1:1 with the finger).
  const dragRef = useRef({ y0: null, dy: 0 });
  function onGrabPointerDown(e) {
    if (!open) return;
    dragRef.current = { y0: e.clientY, dy: 0 };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  }
  function onGrabPointerMove(e) {
    if (dragRef.current.y0 == null) return;
    const dy = Math.max(0, e.clientY - dragRef.current.y0);
    dragRef.current.dy = dy;
    if (sheetRef.current) sheetRef.current.style.transform = `translateX(-50%) translateY(${dy}px)`;
    if (backSheetRef?.current) {
      const p = Math.min(1, dy / 300);
      backSheetRef.current.style.transform = `${backSheetBaseTransform} translateY(${-10 + 10 * p}px) scale(${0.93 + 0.07 * p})`.trim();
      backSheetRef.current.style.filter = `brightness(${0.82 + 0.18 * p})`;
    }
  }
  function onGrabPointerUp() {
    if (dragRef.current.y0 == null) return;
    const dy = dragRef.current.dy;
    dragRef.current.y0 = null;
    if (sheetRef.current) sheetRef.current.style.transition = "";
    if (backSheetRef?.current) { backSheetRef.current.style.transform = ""; backSheetRef.current.style.filter = ""; }
    if (dy > 120) onClose?.();
    else if (sheetRef.current) sheetRef.current.style.transform = "";
  }

  // Tapping outside never closes — the top sheet nudges and a toast explains
  // how to actually go back.
  function onOutsideTap() {
    if (reduced) { setToastShown(true); clearTimeout(toastTimerRef.current); toastTimerRef.current = setTimeout(() => setToastShown(false), 1600); return; }
    setNudging(false);
    requestAnimationFrame(() => setNudging(true));
    setToastShown(true);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastShown(false), 1600);
  }

  if (!shouldRender) return null;

  return (
    <>
      <style>{"@keyframes nlc-stacked-nudge{0%{transform:translateX(-50%) translateY(0)}35%{transform:translateX(-50%) translateY(-14px)}70%{transform:translateX(-50%) translateY(3px)}100%{transform:translateX(-50%) translateY(0)}}"}</style>
      {/* Second scrim — covers the back sheet, not the sermon sheet itself. */}
      <div onClick={onOutsideTap} aria-hidden="true"
        style={{ position: "fixed", inset: 0, zIndex: zIndex - 1, background: open ? "rgba(20,18,15,0.35)" : "rgba(20,18,15,0)", transition: reduced ? "none" : "background .35s ease", pointerEvents: open ? "auto" : "none" }}/>
      <div ref={sheetRef} className="sheet" tabIndex={-1}
        onAnimationEnd={() => setNudging(false)}
        style={{
          zIndex, top: 32,
          transform: open ? "translateX(-50%) translateY(0)" : "translateX(-50%) translateY(105%)",
          transition: reduced ? "none" : SLIDE_TRANSITION,
          animation: !reduced && nudging ? "nlc-stacked-nudge .42s ease" : "none",
          boxShadow: "0 -12px 40px rgba(0,0,0,0.25)",
          outline: "none",
        }}
        role="dialog" aria-modal="true" aria-hidden={!open}>
        <div className="shandle"
          onPointerDown={onGrabPointerDown} onPointerMove={onGrabPointerMove} onPointerUp={onGrabPointerUp} onPointerCancel={onGrabPointerUp}
          style={{ cursor: "grab", touchAction: "none", padding: "9px 0 5px", boxSizing: "content-box" }}/>
        {children}
      </div>
      {toastShown && (
        <div role="status" style={{ position: "fixed", left: "50%", top: 90, transform: "translateX(-50%)", zIndex: zIndex + 1, background: "var(--ink)", color: "#fff", fontSize: 12.5, fontWeight: 700, borderRadius: 999, padding: "8px 14px", whiteSpace: "nowrap", pointerEvents: "none", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          Use ✕ or drag down to go back to {backLabel}
        </div>
      )}
    </>
  );
}
